import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { getRequest, deleteRequest } from "services/api";
import { usePaginationWithComponent } from "hooks/usePaginationWithComponent";
import { buildQueryString } from "utils/queryHelpers";
import { formatDateToLocal } from "utils/utils";

const AttendanceContext = createContext();

export const AttendanceProvider = ({ children, userRoleFilter }) => {
  const [attendance, setAttendance] = useState({
    data: [],
    pagination: null,
    statistics: null,
    error: null,
    loading: false,
  });

  const [filterOptions, setFilterOptions] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const paramStart = params.get("startDate");
    const paramEnd = params.get("endDate");
    
    const now = new Date();
    const currentDay = now.getDate();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    
    // Auto deteksi ganti bulan di tgl 26
    let defaultStart, defaultEnd;
    if (currentDay >= 26) {
      defaultStart = new Date(currentYear, currentMonth, 26);
      defaultEnd = new Date(currentYear, currentMonth + 1, 25);
    } else {
      defaultStart = new Date(currentYear, currentMonth - 1, 26);
      defaultEnd = new Date(currentYear, currentMonth, 25);
    }

    const parseLocalDate = (dateStr) => {
      if (!dateStr) return null;
      const [y, m, d] = dateStr.split("-").map(Number);
      return new Date(y, m - 1, d);
    };

    return {
      search: params.get("search") || "",
      userId: params.get("userId") || "",
      departmentId: params.get("departmentId") || "",
      currentPage: parseInt(params.get("page")) || 1,
      perPage: 100,
      include: "user",
      startDate: paramStart ? parseLocalDate(paramStart) : defaultStart,
      endDate: paramEnd ? parseLocalDate(paramEnd) : defaultEnd,
    };
  });

  // Sync filterOptions to URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    
    if (filterOptions.startDate) {
      params.set("startDate", formatDateToLocal(filterOptions.startDate));
    }
    
    if (filterOptions.endDate) {
      params.set("endDate", formatDateToLocal(filterOptions.endDate));
    }

    if (filterOptions.search) params.set("search", filterOptions.search);
    else params.delete("search");

    if (filterOptions.userId) params.set("userId", filterOptions.userId);
    else params.delete("userId");

    if (filterOptions.departmentId) params.set("departmentId", filterOptions.departmentId);
    else params.delete("departmentId");

    if (filterOptions.currentPage !== 1) params.set("page", filterOptions.currentPage);
    else params.delete("page");

    const newRelativePathQuery = window.location.pathname + "?" + params.toString();
    window.history.replaceState(null, "", newRelativePathQuery);
  }, [filterOptions]);

  const queryString = buildQueryString({
    search: filterOptions.search || "",
    "filter[user_id]": filterOptions.userId || null,
    // page: filterOptions.currentPage,
    // per_page: filterOptions.perPage,
    // limit: filterOptions.perPage,
    all: 1,
    include: filterOptions.include || "user",
    "filter[start_date]": filterOptions.startDate ? formatDateToLocal(filterOptions.startDate) : null,
    "filter[end_date]": filterOptions.endDate ? formatDateToLocal(filterOptions.endDate) : null,
  });

  const fetchAttendance = useCallback(async () => {
    setAttendance((prev) => ({ ...prev, loading: true }));

    try {
      const response = await getRequest(`/ahr/absensis${queryString}`);
      if (response.success) {
        // Temporarily disable role filtering since attendance API doesn't include user roles
        let filteredData = response.data;
        // if (userRoleFilter) {
        //   console.log('Filtering attendance with userRoleFilter:', userRoleFilter);
        //   console.log('Sample attendance record user:', response.data[0]?.user);
        //   filteredData = response.data.filter(record => {
        //     const hasRoles = record.user && record.user.roles && record.user.roles.some(role => role.name === userRoleFilter);
        //     console.log(`User ${record.user?.name} has ${userRoleFilter} role:`, hasRoles);
        //     return hasRoles;
        //   });
        //   console.log('Filtered data length:', filteredData.length);
        // }
        setAttendance({
          data: filteredData,
          pagination: response.pagination,
          statistics: response.statistics,
          error: null,
          loading: false,
        });
      } else {
        throw new Error("Failed to fetch attendance data");
      }
    } catch (error) {
      setAttendance((prev) => ({
        ...prev,
        error: error.message,
        loading: false,
      }));
    }
  }, [queryString]);

  useEffect(() => {
    fetchAttendance();
  }, [fetchAttendance]);

  const handleSearch = (e) => {
    if (e.key === "Enter") {
      const searchValue = e.target.value.trim();
      if (searchValue !== filterOptions.search) {
        setFilterOptions((prev) => ({
          ...prev,
          search: searchValue,
          currentPage: 1,
        }));
      }
    }
  };

  const handlePageChange = (page) => {
    setFilterOptions((prev) => ({ ...prev, currentPage: page }));
  };

  const handlePerPageChange = (perPage) => {
    setFilterOptions((prev) => ({ ...prev, perPage: perPage, currentPage: 1 }));
  };

  const Pagination = usePaginationWithComponent({
    totalPages: attendance.pagination?.last_page || 1,
    currentPage: filterOptions.currentPage,
    onPageChange: handlePageChange,
    visiblePages: 5,
  });

  const deleteAttendance = useCallback(async (id) => {
    try {
      const response = await deleteRequest(`/ahr/absensis/${id}`);
      return response;
    } catch (error) {
      throw error;
    }
  }, []);

  return (
    <AttendanceContext.Provider
      value={{
        attendance,
        filterOptions,
        setFilterOptions,
        handleSearch,
        handlePerPageChange,
        refetch: () => fetchAttendance(),
        deleteAttendance,
        Pagination,
      }}
    >
      {children}
    </AttendanceContext.Provider>
  );
};

export const useAttendance = () => {
  const context = useContext(AttendanceContext);
  if (!context) {
    throw new Error("useAttendance must be used within AttendanceProvider");
  }
  return context;
};
